import {
    ContentDetailInfoShell,
    ContentDetailThreeColLayout,
    DetailKvListValue,
    DetailKvTable,
    DetailPanel,
    DetailSummaryButton,
    KV_LIST_TYPES,
} from '../../../../components/detailLayout';
import React from "react";
import Th_title from "../../../../components/Th_title";
import ReadOnlyInputBasic from "../../../../components/input/ReadOnlyInputBasic";
import {removeInspectionHeaders} from "../../../../api/maintenance/InspectionService";
import {inspectCycle, inspectionDay, inspectionWeek} from "../InspectionHeaderCreate";
import {inspectionRequestCode} from "../InspectionCreate";
import {useNavigate} from "react-router-dom";
import {MemoList} from "../../../../components/memo/MemoList";
import SimmsDatePicker from "../../../../components/datepicker/SimmsDatePicker";
import {isMobile} from "../../../../components/commonmethod/CommonMethod";
import {InspectionHeaderProgressTrack} from "./InspectionHeaderProgressTrack";

function formatCycleText(inspection, {includeDeadline = false} = {}) {
    if (!inspection?.inspectCycle) return "";

    const cycle = inspectCycle.find((data) => data.id === inspection.inspectCycle)?.name ?? inspection.inspectCycle;
    const cycleShort = String(cycle).replace(/\s*\([^)]*\)\s*/g, "").trim() || cycle;
    const week = inspection.inspectCycle !== "매주(week)" && inspection.week
        ? inspectionWeek.find((data) => data.id === inspection.week)?.name ?? inspection.week
        : "";
    const day = inspection.day
        ? inspectionDay.find((data) => data.id === inspection.day)?.name ?? inspection.day
        : "";
    const deadline = includeDeadline && inspection.periodDateLimit != null && inspection.periodDateLimit !== ""
        ? `기한 ${inspection.periodDateLimit}일`
        : "";

    const details = [week, day, deadline].filter(Boolean);
    return details.length > 0 ? `${cycleShort} (${details.join(" · ")})` : cycleShort;
}

function InspectionHeaderInfo({id, inspection, assets, handler, childInspections = []}) {
    const navigate = useNavigate();

    const remove = async () => {
        await removeInspectionHeaders({ids: [id]}).then((response) => {
            if (response.status === 200) {
                alert("정기점검이 정상적으로 삭제되었습니다.");
                navigate("/maintenance/inspection_header");
            }
        });
    };

    const canUpdate = inspection.auth?.update;

    const summaryMeta = [
        inspection.inspectCode?.name && `코드 ${inspection.inspectCode.name}`,
        inspection.requester?.name && `계획자 ${inspection.requester.name}`,
        formatCycleText(inspection, {includeDeadline: true}),
    ].filter(Boolean).join(" · ");

    const requestInfoPanel = (
        <DetailPanel title={"점검요청정보"}>
            <DetailKvTable>
                <tbody>
                    <tr>
                        <Th_title title={"자산목록"} colspan={3} className="fieldset-td relative align-top">
                            <DetailKvListValue items={assets} listType={KV_LIST_TYPES.asset} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"등록일시"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"createdAt"} value={inspection.createdAt} />
                        </Th_title>
                        <Th_title title={"등록자"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"createdBy"} value={inspection.createdBy} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"수정일시"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"updatedAt"} value={inspection.updatedAt} />
                        </Th_title>
                        <Th_title title={"수정자"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"updatedBy"} value={inspection.updatedBy} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"점검 제목"} colspan={3} className="fieldset-td">
                            <ReadOnlyInputBasic target={"title"} value={inspection.title} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"요청자"} colspan={3} className="fieldset-td">
                            <ReadOnlyInputBasic target={"requester"} value={inspection.requester} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"점검코드"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"inspectCode"} value={inspection.inspectCode?.name} />
                        </Th_title>
                        <Th_title title={"요청방식"} colspan={1} className="fieldset-td">
                            <ReadOnlyInputBasic target={"requestCode"} value={inspectionRequestCode.find((data) => data.id === inspection.requestCode)} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"점검기간"} colspan={3} className="fieldset-td fieldset-td-input">
                            <div className="web-flex asset-detail-date-range">
                                <SimmsDatePicker data={inspection.inspectStartDate} readonly />
                                <span className="asset-detail-date-range-sep">~</span>
                                <SimmsDatePicker data={inspection.inspectEndDate} readonly />
                            </div>
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"점검주기"} colspan={3} className="fieldset-td fieldset-td-input">
                            <ReadOnlyInputBasic target={"inspectCycle"} value={formatCycleText(inspection)} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"정기점검 기한(점검별)"} colspan={3} className="fieldset-td fieldset-td-input">
                            <ReadOnlyInputBasic target={"periodDateLimit"} value={`${inspection.periodDateLimit}일`} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"SLA 사용여부"} colspan={3} className="fieldset-td">
                            <ReadOnlyInputBasic target={"useSla"} value={inspection.useSla ? "사용함" : "사용 안함"} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"비고"} colspan={3} className="fieldset-td">
                            <div className="align-text-div">{inspection.etc}</div>
                        </Th_title>
                    </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );

    const statusPanel = (
        <DetailPanel title={"점검 요청 진행상태"}>
            <DetailKvTable>
                <tbody>
                    <tr>
                        <Th_title title={"담당자"} colspan={3} className="fieldset-td fieldset-td-input relative align-top">
                            <DetailKvListValue items={handler} listType={KV_LIST_TYPES.user} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"검수자"} colspan={3} className="fieldset-td">
                            <ReadOnlyInputBasic target={"inspector"} value={inspection.inspector} />
                        </Th_title>
                    </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );

    const memoPanel = (
        <MemoList entityId={inspection.id} entityName={"Inspection_Header"} />
    );

    return (
        <ContentDetailInfoShell
            title={inspection.title || "정기점검 상세"}
            meta={summaryMeta || undefined}
            scrollMode="columns"
            listUrl="/maintenance/inspection_header"
            topContent={(
                <InspectionHeaderProgressTrack inspections={childInspections} />
            )}
            actions={
                canUpdate ? (
                    <DetailSummaryButton onClick={() => navigate(`/maintenance/inspection_header/update/${id}`)}>
                        수정
                    </DetailSummaryButton>
                ) : null
            }
            rollup={{
                remove: !isMobile && inspection.auth?.update && remove,
            }}
        >
            <ContentDetailThreeColLayout
                primary={requestInfoPanel}
                secondary={statusPanel}
                aside={memoPanel}
            />
        </ContentDetailInfoShell>
    );
}

export {InspectionHeaderInfo};
